import { useCallback, useEffect, useRef, useState } from 'react'

import { useAppDispatch, useAppSelector } from '@App/hooks'
import { useWatchlists } from '@App/hooks/useWatchList'
import { MODAL_TYPES } from '@App/redux/slices/modals/enums'
import { setModal } from '@App/redux/slices/modals/ModalSlice'
import { CloseIcon } from '@App/static/icons/ArrowIcons'

import {
  useSpotOrderMarketsTableData,
  useSpotOrderMarketsTableFilters,
} from '../../../pages/spot/components/SpotOrderMarketsTable/model'

import { ModalTypes } from '../modalTypes'
import ModalWrapper from '../ModalWrapper'

import { WatchlistDetails } from './components/WatchlistDetails'
import { WatchlistsSidebar } from './components/WatchlistsSidebar'
import { selectFavouriteMarketIds, selectWatchlists } from '@App/redux/slices/watchlist/selectors'
import { selectSharedCountries } from '@Redux/slices/config/config.selectors'
import s from './styles.module.scss'
import { clearMarkets, getMarkets, requestMarkets, requestMarketsByIds } from '@Redux/slices/sockets/PublicServiceConnectionSlice'
import { mapTabToBackendFilter } from '@Redux/interfaces'
import { Watchlist } from '@Redux/slices/watchlist/slice'
import { useSelector } from 'react-redux'

export const EditWatchlistsModal = ({ isOpen, setIsOpen, styles }: ModalTypes) => {
  const dispatch = useAppDispatch()
  const containerRef = useRef<HTMLDivElement | null>(null)
  const markets = useAppSelector((state) => getMarkets(state))
  const favouriteMarketIds = useAppSelector(selectFavouriteMarketIds)
  const watchlistsMap = useSelector(selectWatchlists)
  const sharedCountries = useAppSelector(selectSharedCountries)

  const { updatedMarkets, openedTradesMarkets, available_types } = useSpotOrderMarketsTableData({
    markets,
  })

  const {
    watchlists,
    setActive,
    setActiveForEdit,
    activeWatchlistId,
    reorder,
    delete: deleteCustomWatchlist,
    rename,
    isMarketInWatchlist,
    removeMarket,
    moveMarket,
  } = useWatchlists({
    showPortfolio: true,
    markets: updatedMarkets,
    sharedCountries,
  })

  const activeWatchList = watchlists.find((w) => w.key === activeWatchlistId) ?? null
  const [selectedWatchListItemId, setSelectedWatchListItemId] = useState<string | null>(null)
  const [selectedSubfilterId, setSelectedSubfilterId] = useState<string | null>(null)

  let actualTab =
    !!activeWatchList && activeWatchList?.items?.length > 0
      ? `${activeWatchlistId}-${selectedWatchListItemId}`
      : activeWatchlistId

  if (activeWatchlistId === 'commods') {
    actualTab = selectedWatchListItemId
  }

  if (activeWatchlistId === 'stocks') {
    if (selectedSubfilterId) {
      actualTab = selectedSubfilterId
    } else if (selectedWatchListItemId === 'popular_shares') {
      actualTab = 'stocks'
    } else {
      actualTab = `stocks|${selectedWatchListItemId}`
    }
  }

  const getLocalSelectedTabWatchlist = useCallback((): Watchlist | null => {
    if (!actualTab) return null

    if (watchlistsMap[actualTab]) {
      return watchlistsMap[actualTab]
    }

    const baseId = actualTab.split('-')[0]
    if (watchlistsMap[baseId]) {
      return watchlistsMap[baseId]
    }

    const found = Object.values(watchlistsMap).find(
      (w) => w.key === actualTab || w.key.startsWith(actualTab + '-'),
    )

    return found ?? null
  }, [actualTab, watchlistsMap])

  const backendFilter = actualTab ? mapTabToBackendFilter(actualTab) : 'all'

  const selectedWatchlist = getLocalSelectedTabWatchlist()
  const isCustomWatchlist = selectedWatchlist?.isCustom ?? false
  const isFavouritesTab = actualTab === 'favourites'
  const shouldLoadByIds = (isCustomWatchlist || isFavouritesTab) && (selectedWatchlist?.market_ids?.length ?? 0) > 0

  useEffect(() => {
    if (!actualTab) return

    dispatch(clearMarkets())

    if (shouldLoadByIds && selectedWatchlist?.market_ids?.length) {
      dispatch(requestMarketsByIds({ ids: selectedWatchlist.market_ids }))
      return
    }

    dispatch(requestMarkets({ filter: backendFilter }))
  }, [actualTab, shouldLoadByIds, selectedWatchlist?.market_ids, backendFilter, dispatch])

  const { show_markets: _show_markets } = useSpotOrderMarketsTableFilters({
    available_types,
    selectedTab: actualTab,
    updatedMarkets,
    openedTradesMarkets,
    isMarketInWatchlist,
    getSelectedTabWatchlist: getLocalSelectedTabWatchlist,
    favouriteMarketIds,
  })


  const handleSetSelectedWatchListItemId = useCallback((id: string | null) => {
    setSelectedWatchListItemId(id)
    setSelectedSubfilterId(null)
  }, [])

  useEffect(() => {
    if (activeWatchList && activeWatchList?.items?.length > 0) {
      handleSetSelectedWatchListItemId(activeWatchList?.items?.[0].key)
    } else {
      handleSetSelectedWatchListItemId(null)
    }
  }, [activeWatchList?.key])
  let show_markets = _show_markets
  if (activeWatchList?.market_ids?.length) {
    const orderMap = new Map(activeWatchList.market_ids.map((id, i) => [id, i]))
    show_markets = [...show_markets].sort((a, b) => (orderMap.get(a.id) ?? Infinity) - (orderMap.get(b.id) ?? Infinity))
  }
  return (
    <ModalWrapper
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      styles={{
        ...styles,
        content: {
          ...styles?.content,
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          paddingTop: '50px',
          paddingLeft: '70px',
        },
      }}
    >
      <div className={s.container} ref={containerRef}>
        <div className={s.header}>
          Edit watchlists
          <CloseIcon className={s.closeIcon} onClick={setIsOpen} />
        </div>

        <div className={s.content}>
          <WatchlistsSidebar
            watchlists={watchlists}
            activeWatchlistId={activeWatchlistId}
            onSelect={setActiveForEdit}
            onToggleActive={setActive}
            onReorder={reorder}
            onCreateNew={() => dispatch(setModal({ modalType: MODAL_TYPES.ADD_WATCHLIST, useStack: true }))}
          />
          {activeWatchlistId && (
            <WatchlistDetails
              selectedWatchListItemId={selectedWatchListItemId}
              setSelectedWatchListItemId={handleSetSelectedWatchListItemId}
              key={activeWatchlistId}
              watchlist={activeWatchList}
              markets={show_markets.slice(0, 20)}
              onDelete={(id) => deleteCustomWatchlist(id)}
              onEditName={rename}
              onRemoveMarket={(marketId) => removeMarket(activeWatchlistId, marketId)}
              onMoveMarket={(marketId, newOrder) => moveMarket(activeWatchlistId, marketId, newOrder)}
              onSubfilterSelect={setSelectedSubfilterId}
              selectedSubfilterId={selectedSubfilterId}
            />
          )}
        </div>
      </div>
    </ModalWrapper>
  )
}
